import React, { useMemo } from 'react';
import leagueThresholds from '../config/leagueThresholds.json';

// Umbrales globales (fallback) para métricas aún no calibradas por liga.
// TODO automatización: percentiles por-liga desde Firebase + unidades alineadas.
const GLOBAL_THRESHOLDS = {
  fg_pct:    { p33: 0.42, p66: 0.48 },
  fg3_pct:   { p33: 0.30, p66: 0.40 },
  fta:       { p33: 10,   p66: 20 },
};

const byLeague = leagueThresholds.reduce((acc, e) => {
  acc[e.liga.toUpperCase()] = e;
  return acc;
}, {});

const num = (v) => (v === null || v === undefined || v === '' || isNaN(Number(v)) ? null : Number(v));
// Normaliza % a 0-1 (acepta 0-1 o 0-100).
const toFrac = (v) => { const n = num(v); return n === null ? null : (n > 1 ? n / 100 : n); };

export default function LeagueRangesTable({ liga, values }) {
  const d = useMemo(() => (liga ? byLeague[liga.toUpperCase()] || null : null), [liga]);

  if (!d) {
    return (
      <div className="lr2">
        <div className="lr2-head">
          <span className="lr2-title">Rangos de la liga</span>
          {liga && <span className="lr2-liga">{liga}</span>}
        </div>
        <div className="lr2-empty">
          Aún no hay suficientes muestras de <b>{liga || 'esta liga'}</b> para calibrar rangos confiables.
        </div>
        <style>{lr2Styles}</style>
      </div>
    );
  }

  const pace = (v) => v.toFixed(0);
  const ef = (v) => v.toFixed(2);
  const pct = (v) => `${Math.round(v * 100)}%`;
  const int = (v) => String(Math.round(v));
  const G = GLOBAL_THRESHOLDS;
  const v = values || {};

  const metrics = [
    { label: 'Ritmo partido',       val: num(v.pace),      p33: d.pace_p33, p66: d.pace_p66, fmt: pace },
    { label: 'Eficiencia ofensiva', val: num(v.ef),        p33: d.ef_p33,   p66: d.ef_p66,   fmt: ef },
    { label: '% Tiros de campo',    val: toFrac(v.fg_pct),  p33: G.fg_pct.p33,    p66: G.fg_pct.p66,    fmt: pct },
    { label: '% Triples',           val: toFrac(v.fg3_pct), p33: G.fg3_pct.p33,   p66: G.fg3_pct.p66,   fmt: pct },
    { label: 'Tiros libres',        val: num(v.fta),       p33: G.fta.p33,  p66: G.fta.p66,  fmt: int },
  ];

  const bucketOf = (val, p33, p66) => (val == null ? null : val < p33 ? 'lo' : val <= p66 ? 'mid' : 'hi');
  // tono por segmento (verde=bueno/rojo=malo); inverted → bajo es bueno.
  const tone = (seg, inverted) => (seg === 'mid' ? 'mid' : seg === (inverted ? 'lo' : 'hi') ? 'good' : 'bad');
  const wordOf = { lo: 'BAJO', mid: 'MEDIO', hi: 'ALTO' };

  return (
    <div className="lr2">
      <div className="lr2-head">
        <span className="lr2-title">Rangos de la liga</span>
        <span className="lr2-liga">{liga}</span>
      </div>
      <div className="lr2-sample">Calibrado con {d.n.toLocaleString('es-CL')} muestras · ● en vivo = este partido</div>

      <div className="lr2-list">
        {metrics.map((m) => {
          const b = bucketOf(m.val, m.p33, m.p66);
          const segs = [
            { k: 'lo',  txt: `<${m.fmt(m.p33)}` },
            { k: 'mid', txt: `${m.fmt(m.p33)}–${m.fmt(m.p66)}` },
            { k: 'hi',  txt: `>${m.fmt(m.p66)}` },
          ];
          const valTone = b ? tone(b, m.inverted) : null;
          return (
            <div className="lr2-metric" key={m.label}>
              <div className="lr2-top">
                <span className="lr2-label">
                  {m.label}
                  {m.inverted && <span className="lr2-inv" title="Menos pérdidas = mejor">↓</span>}
                </span>
                {m.val != null && (
                  <span className={`lr2-live ${valTone}`}>
                    <span className="lr2-live-dot" />
                    {m.fmt(m.val)} · {wordOf[b]}
                  </span>
                )}
              </div>
              <div className="lr2-segs">
                {segs.map((s) => (
                  <span
                    key={s.k}
                    className={`lr2-seg ${tone(s.k, m.inverted)} ${b === s.k ? 'active' : (b ? 'dim' : '')}`}
                  >
                    <i>{wordOf[s.k]}</i><b>{s.txt}</b>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <style>{lr2Styles}</style>
    </div>
  );
}

const lr2Styles = `
  .lr2 { }
  .lr2-head { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; }
  .lr2-title { font-size: 10px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--muted); }
  .lr2-liga { font-size: 13px; font-weight: 700; color: var(--ink); }
  .lr2-sample { font-size: 9.5px; color: var(--muted); margin: 3px 0 14px; }
  .lr2-empty { font-size: 12px; color: var(--muted); line-height: 1.5; padding: 8px 0; }
  .lr2-list { display: flex; flex-direction: column; gap: 18px; }
  .lr2-metric { display: flex; flex-direction: column; gap: 7px; }
  .lr2-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  .lr2-label { font-size: 12.5px; font-weight: 600; color: var(--ink); display: flex; align-items: center; gap: 5px; }
  .lr2-inv { font-size: 10px; color: var(--muted); }
  .lr2-live { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 700;
    padding: 3px 9px; border-radius: 999px; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .lr2-live.good { color: #15803d; background: rgba(22,163,74,.12); }
  .lr2-live.mid  { color: #b45309; background: rgba(217,119,6,.12); }
  .lr2-live.bad  { color: #b91c1c; background: rgba(220,38,38,.11); }
  .lr2-live-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor;
    animation: lr2-pulse 1.5s infinite; }
  @keyframes lr2-pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
  .lr2-segs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5px; }
  .lr2-seg { display: flex; flex-direction: column; align-items: center; gap: 2px;
    padding: 9px 5px; border-radius: 9px; text-align: center; border: 1.5px solid transparent;
    transition: opacity .15s; }
  .lr2-seg i { font-size: 8.5px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
    font-style: normal; opacity: .8; }
  .lr2-seg b { font-size: 12.5px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .lr2-seg.good { background: rgba(22,163,74,.10); color: #15803d; }
  .lr2-seg.mid  { background: rgba(217,119,6,.10);  color: #b45309; }
  .lr2-seg.bad  { background: rgba(220,38,38,.09);   color: #b91c1c; }
  .lr2-seg.dim  { opacity: .42; }
  .lr2-seg.active { opacity: 1; border-color: currentColor; box-shadow: 0 0 0 1px currentColor inset; }

  /* 2026-06-01: en dark, los verdes/amber/rojos oscuros del light se
     hunden contra el fondo negro y los rgba .09-.12 dan superficies casi
     invisibles. Subimos a las versiones claras del dark token system
     (--pos/--warn/--neg ya configuradas: #30D158/#FFD60A/#FF6B5B) +
     alpha de fondo .20-.22 para que las cajas se distingan claramente.
     Opacity dim sube de .42 → .60 — atenuado pero legible. */
  [data-theme="dark"] .lr2-seg.good { background: rgba(48,209,88,.20);  color: #5FE08C; }
  [data-theme="dark"] .lr2-seg.mid  { background: rgba(255,214,10,.18); color: #FFE34A; }
  [data-theme="dark"] .lr2-seg.bad  { background: rgba(255,107,91,.20); color: #FF8B7E; }
  [data-theme="dark"] .lr2-seg.dim  { opacity: .60; }
  [data-theme="dark"] .lr2-seg.active { box-shadow: 0 0 0 1.5px currentColor inset; }

  /* Pills "X · BAJO/MEDIO/ALTO" con el valor en vivo: mismos colores. */
  [data-theme="dark"] .lr2-live.good { background: rgba(48,209,88,.18);  color: #5FE08C; }
  [data-theme="dark"] .lr2-live.mid  { background: rgba(255,214,10,.16); color: #FFE34A; }
  [data-theme="dark"] .lr2-live.bad  { background: rgba(255,107,91,.18); color: #FF8B7E; }
`;
